import {useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {useSelectedTab} from '@Components/tabs';
import {addItemsToGrid, deleteGridItems, moveGridItem} from '@Components/base-grid/base-grid-reducer';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridCollectionItemStorage} from '@Components/base-grid/components/grid-collection-item';
import type {ItemsMovedToFolder, UserCollectionsStorage} from '@Libraries/user-media-library';
import {
  getRootUserCollection,
  getUniqueNewFolderName,
  getUserCollectionName,
  getUserCollections,
  moveUserItemsToCollection,
  saveUserCollection,
  showMovedToFolderGrowl,
} from '@Libraries/user-media-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {openErrorModal} from '@Modals/error-modal';
import {useIsMounted} from '@Hooks/useIsMounted';
import {getFolderGridItemNames, getGridIdForTabStates, getNewCollectionButtonGridItem, getNewFolderGridIndex, getSavedFolderGridIndex} from '@Panels/user-media-panel/user-media.library';
import type {CollectionToAddToGrid, SavedFolderToPlace} from '@Panels/user-media-panel/user-media-panel.types';
import type {MoveToFolderPicker, MoveToFolderPickerParams} from '@Panels/user-media-panel/components/move-to-folder-grid/move-to-folder-grid.types';
import {
  COLLECTION_GRID_ID,
  COLLECTION_MEDIA_GRID_ID,
  NewFolderGridPlacement,
  UploadsMediaSubTab,
  UploadsMediaType,
  USER_UPLOADS_MEDIA_SUB_TABS,
  USER_UPLOADS_MEDIA_TABS,
  USER_UPLOADS_SELECTION_GRID_ID,
} from '@Panels/user-media-panel/user-media-panel.types';
import {useAppDispatch, useAppSelector} from '@/hooks';

/** Everything the folders grid lists: folder tiles, the desktop create-folder tile and, while loading, skeletons. Undefined until it first loads. */
export const useFolderGridItems = (): GridItemStorage[] | undefined => {
  return useAppSelector((state) => {
    return state.grids.gridsHashmap[COLLECTION_GRID_ID]?.items;
  });
};

/**
 * Adds a new, not-yet-saved "Untitled" folder tile to the collections grid; the tile opens in rename
 * mode and saves on commit. It always leads the grid while it is named, whatever the sort order, so the user
 * finds it in the same place every time; once saved it moves to where the sort order lists its name
 * (useMoveSavedFolderToSortedPosition). Anonymous users get the signup modal instead (folders need an account).
 */
export const useCreateCollection = (isSmallScreen: boolean): (() => void) => {
  const folderGridItems = useFolderGridItems();
  const addCollectionToGrid = useAddCollectionToGrid(isSmallScreen ? [] : [getNewCollectionButtonGridItem()], NewFolderGridPlacement.LEADING);

  return (): void => {
    if (!isUserLoggedIn()) {
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: 'default',
      });
      return;
    }
    addCollectionToGrid({collectionId: '', collectionName: getUniqueNewFolderName(getFolderGridItemNames(folderGridItems))});
  };
};

/** useAddCollectionToGrid for a folder the Move popup saved: the large-screen folders grid leads with the create-folder tile. */
export const useAddCollectionToLargeScreenGrid = (): ((collection: CollectionToAddToGrid) => void) => {
  return useAddCollectionToGrid([getNewCollectionButtonGridItem()], NewFolderGridPlacement.SORTED);
};

/** useAddCollectionToGrid for a folder the phone Move panel saved: the small-screen create-folder button sits above the grid. */
export const useAddCollectionToSmallScreenGrid = (): ((collection: CollectionToAddToGrid) => void) => {
  return useAddCollectionToGrid([], NewFolderGridPlacement.SORTED);
};

/**
 * Moves a folder tile that just got saved to where the active sort order lists its name (see getSavedFolderGridIndex).
 * The grid is read at call time, because the save that precedes the move is asynchronous and the tiles may have changed meanwhile.
 */
export const useMoveSavedFolderToSortedPosition = (): ((savedFolder: SavedFolderToPlace) => void) => {
  const dispatch = useAppDispatch();

  return ({folderId, folderName}: SavedFolderToPlace): void => {
    const state = window.PMW.redux.store.getState();
    dispatch(
      moveGridItem({
        gridId: COLLECTION_GRID_ID,
        itemId: folderId,
        index: getSavedFolderGridIndex({folderGridItems: state.grids.gridsHashmap[COLLECTION_GRID_ID]?.items, folderId, folderName, sortOrder: state.mediaUploads.sortOrder}),
      })
    );
  };
};

/**
 * Inserts a folder into the collections grid, whether it was created inline, in the Move popup or in the phone Move
 * panel. `placement` says whether it leads the grid or takes the position the active sort order dictates (see
 * getNewFolderGridIndex). An empty collectionId marks an unsaved tile that GridCollectionItem opens in rename mode;
 * a saved folder's id doubles as its grid item id, exactly like server-loaded folders. `leadingGridItems` are the
 * tiles the layout lists ahead of every folder; a grid with no items yet gets them too.
 */
const useAddCollectionToGrid = (leadingGridItems: GridItemStorage[], placement: NewFolderGridPlacement): ((collection: CollectionToAddToGrid) => void) => {
  const folderGridItems = useFolderGridItems();
  const sortOrder = useAppSelector((state) => {
    return state.mediaUploads.sortOrder;
  });
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.IMAGE) as UploadsMediaType;
  const dispatch = useAppDispatch();

  const getNewCollectionIndex = (collectionName: string): number | undefined => {
    if (placement === NewFolderGridPlacement.LEADING) {
      return leadingGridItems.length;
    }
    return getNewFolderGridIndex({folderGridItems, sortOrder, newFolderName: collectionName, leadingGridItemsCount: leadingGridItems.length});
  };

  return ({collectionId, collectionName}: CollectionToAddToGrid): void => {
    const newCollection: GridCollectionItemStorage = {
      id: collectionId || uuidv4(),
      collectionId,
      type: GRID_ITEM_TYPE.COLLECTION,
      collectionName,
      multiMediaType: activeTab,
      isShared: false,
    };

    dispatch(
      addItemsToGrid({
        id: COLLECTION_GRID_ID,
        items: folderGridItems?.length ? [newCollection] : [...leadingGridItems, newCollection],
        index: getNewCollectionIndex(collectionName),
      })
    );
  };
};

/**
 * The state behind both Move-to-folder surfaces, the desktop popup and the phone sheet, which each live exactly
 * as long as the picker is open: it loads the folders, tracks the chosen one, creates a folder in place and moves
 * the selected items there. Answers that arrive after the surface closed still reach the folders grid and the
 * moved-items handler, but never the picker that is gone.
 */
export const useMoveToFolderPicker = ({
  multimediaType,
  currentCollectionId,
  selectedItems,
  onMoveItems,
  onClose,
  addCollectionToGrid,
}: MoveToFolderPickerParams): MoveToFolderPicker => {
  const [loadedFolders, setLoadedFolders] = useState<UserCollectionsStorage[]>();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState<string | null>(null);
  const [isSavingNewFolder, setIsSavingNewFolder] = useState(false);
  const [isMovingItems, setIsMovingItems] = useState(false);
  const isMounted = useIsMounted();
  const rootFolder = getRootUserCollection(multimediaType);

  useEffect(() => {
    let isCurrentRequest = true;
    const loadFolders = async (): Promise<void> => {
      try {
        const folders = await getUserCollections(multimediaType);
        if (isCurrentRequest) {
          setLoadedFolders(folders);
        }
      } catch {
        if (isCurrentRequest) {
          onClose();
          showRequestErrorModal('useMoveToFolderPicker::loadFoldersError');
        }
      }
    };
    void loadFolders();
    return (): void => {
      isCurrentRequest = false;
    };
  }, [multimediaType]);

  const isCurrentFolder = (folder: UserCollectionsStorage): boolean => {
    return folder.id === currentCollectionId;
  };

  const isMoveTarget = (folderId: string | null): folderId is string => {
    return folderId !== null && folderId !== currentCollectionId;
  };

  const toggleFolderSelection = (folderId: string): void => {
    setSelectedFolderId(selectedFolderId === folderId ? null : folderId);
  };

  const startCreatingNewFolder = (): void => {
    if (newFolderName !== null || !loadedFolders) {
      return;
    }
    setNewFolderName(
      getUniqueNewFolderName(
        loadedFolders.map((folder) => {
          return folder.name;
        })
      )
    );
  };

  const saveNewFolder = async (folderName: string): Promise<void> => {
    setNewFolderName(folderName);
    setIsSavingNewFolder(true);
    try {
      const newFolderId = await saveUserCollection({name: folderName, collectionType: multimediaType});
      addCollectionToGrid({collectionId: newFolderId, collectionName: folderName});
      if (isMounted.current) {
        setLoadedFolders((folders) => {
          return [{id: newFolderId, name: folderName, type: multimediaType, isShared: false}, ...(folders ?? [])];
        });
        setSelectedFolderId(newFolderId);
      }
    } catch {
      showRequestErrorModal('useMoveToFolderPicker::createNewFolderError');
    } finally {
      if (isMounted.current) {
        setNewFolderName(null);
        setIsSavingNewFolder(false);
      }
    }
  };

  const commitNewFolderName = (committedName: string): void => {
    if (newFolderName === null) {
      return;
    }
    void saveNewFolder(committedName || newFolderName);
  };

  const cancelCreatingNewFolder = (): void => {
    setNewFolderName(null);
  };

  const moveItemsToFolder = async (targetFolderId: string): Promise<void> => {
    setIsMovingItems(true);
    try {
      const movedItemIds = await moveUserItemsToCollection({multimediaType, selectedItems, currentCollectionId, targetCollectionId: targetFolderId});
      onMoveItems({movedItemIds, folderName: getUserCollectionName([rootFolder, ...(loadedFolders ?? [])], targetFolderId), mediaType: multimediaType});
      if (isMounted.current) {
        onClose();
      }
    } catch {
      openErrorModal({message: window.i18next.t('pmwjs_move_multimedia_error'), log: false});
    } finally {
      if (isMounted.current) {
        setIsMovingItems(false);
      }
    }
  };

  const moveItemsToSelectedFolder = (): void => {
    if (!isMoveTarget(selectedFolderId) || isMovingItems) {
      return;
    }
    void moveItemsToFolder(selectedFolderId);
  };

  return {
    areFoldersLoaded: loadedFolders !== undefined,
    rootFolder: isCurrentFolder(rootFolder) ? null : rootFolder,
    folders: (loadedFolders ?? []).filter((folder) => {
      return !isCurrentFolder(folder);
    }),
    selectedFolderId,
    isMoveTargetSelected: isMoveTarget(selectedFolderId),
    newFolderName,
    isSavingNewFolder,
    isMovingItems,
    toggleFolderSelection,
    startCreatingNewFolder,
    commitNewFolderName,
    cancelCreatingNewFolder,
    moveItemsToSelectedFolder,
  };
};

const showRequestErrorModal = (debugDetails: string): void => {
  openErrorModal({
    message: window.i18next.t('pmwjs_general_ajax_error'),
    debugDetails,
    log: false,
  });
};

/** The grid the uploads panel is listing right now: the open folder's grid, else the grid for the active tab and sub-pill. */
export const useActiveUploadsGridId = (defaultTab: UploadsMediaType): string => {
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, defaultTab) as UploadsMediaType;
  const activeSubtab = useSelectedTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.ALL) as UploadsMediaSubTab;
  return currentCollectionId ? COLLECTION_MEDIA_GRID_ID : getGridIdForTabStates(activeTab, activeSubtab);
};

/** The items of the grid the uploads panel is listing right now. Undefined until that grid first loads. */
export const useActiveUploadsGridItems = (defaultTab: UploadsMediaType): GridItemStorage[] | undefined => {
  const activeUploadsGridId = useActiveUploadsGridId(defaultTab);
  return useAppSelector((state) => {
    return state.grids.gridsHashmap[activeUploadsGridId]?.items;
  });
};

/** Drops items that left the listed grid (moved to another folder, deleted) out of the grid and the uploads selection. */
export const useRemoveItemsFromActiveUploadsGrid = (defaultTab: UploadsMediaType): ((itemIds: string[]) => void) => {
  const dispatch = useAppDispatch();
  const activeUploadsGridId = useActiveUploadsGridId(defaultTab);
  return (itemIds: string[]): void => {
    dispatch(
      deleteGridItems({
        gridId: activeUploadsGridId,
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
        itemIdsToDelete: itemIds,
      })
    );
  };
};

/**
 * What every move surface calls once the server has answered: the moved items leave the listed grid and
 * the "N photos moved to Folder" growl announces where they went. Nothing is announced when nothing moved.
 */
export const useOnItemsMovedToFolder = (defaultTab: UploadsMediaType): ((itemsMovedToFolder: ItemsMovedToFolder) => void) => {
  const removeItemsFromActiveUploadsGrid = useRemoveItemsFromActiveUploadsGrid(defaultTab);
  return (itemsMovedToFolder: ItemsMovedToFolder): void => {
    removeItemsFromActiveUploadsGrid(itemsMovedToFolder.movedItemIds);
    if (!itemsMovedToFolder.movedItemIds.length) {
      return;
    }
    showMovedToFolderGrowl(itemsMovedToFolder);
  };
};
